import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wordmark, LogoMark } from "@/components/Logo";
import Timeline from "@/components/Timeline";
import { Search, ArrowLeft, Loader2, Link2, AlertTriangle, CheckCircle2, Plane, Hash, Calendar, Building2, User, Globe, Activity, MapPin, Download, PackagePlus, ShieldCheck, Clock, X, Package } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PodDocument } from "@/components/ShipmentDocs";
import { motion, AnimatePresence, useScroll, useSpring } from "framer-motion";
import { toast } from "@/lib/toast";
import { celebrate, triggerHaptic } from "@/lib/haptics";

const fmtDT = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

export default function Track() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tn, setTn] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPod, setShowPod] = useState(false);
  const celebrated = useRef("");

  const runSearch = useCallback(async (q) => {
    q = (q || "").trim();
    if (!q) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/track/${encodeURIComponent(q)}`);
      setResult(data);
      triggerHaptic("success");
    } catch (err) {
      triggerHaptic("error");
      setError(err?.response?.status === 404 ? "No shipment found for that tracking number." : "Unable to fetch tracking details. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  const search = (e) => {
    e?.preventDefault();
    const q = tn.trim();
    if (!q) return;
    if (searchParams.get("tn") === q) runSearch(q); else setSearchParams({ tn: q });
  };

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30 });

  useEffect(() => {
    const qp = searchParams.get("tn");
    if (qp) { setTn(qp); runSearch(qp); }
  }, [searchParams, runSearch]);

  useEffect(() => {
    if (!result?.tracking_number) return;
    if (result.delivered && celebrated.current !== result.tracking_number) { celebrated.current = result.tracking_number; celebrate("delivered"); }
  }, [result]);

  const copyLink = () => { navigator.clipboard?.writeText(`${window.location.origin}/track?tn=${result.tracking_number}`); toast.success("Tracking link copied"); };
  const compact = !!result || loading;

  return (
    <div className="relative min-h-screen overflow-hidden">
      <motion.div className="fixed left-0 right-0 top-0 z-[60] h-1 origin-left bg-gradient-to-r from-primary via-cyan-500 to-brand-red" style={{ scaleX }} data-testid="scroll-progress" />
      <div className="pointer-events-none absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-primary/15 blur-3xl animate-blob" />
      <div className="pointer-events-none absolute top-10 right-0 h-80 w-80 rounded-full bg-cyan-400/15 blur-3xl animate-blob" style={{ animationDelay: "4s" }} />
      <div className="pointer-events-none absolute top-[60%] -left-20 h-72 w-72 rounded-full bg-brand-red/10 blur-3xl animate-blob" style={{ animationDelay: "8s" }} />

      <header className="glass sticky top-0 z-30 border-b border-border/80">
        <div className="h-0.5 w-full bg-gradient-to-r from-primary via-cyan-500 to-brand-red" />
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <Link to="/login" className="flex items-center gap-2" data-testid="track-home-link">
            <LogoMark className="h-9 w-9" />
            <Wordmark className="text-xl" />
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/book" className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/10" data-testid="track-book-link"><PackagePlus className="h-4 w-4" /> Book a shipment</Link>
            <Button variant="outline" size="sm" onClick={() => navigate("/login")} data-testid="track-login-link">
              <ArrowLeft className="h-4 w-4" /> Login
            </Button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-6xl px-4 pb-16 pt-8 sm:px-6 sm:pt-12">
        <motion.div layout className={`mx-auto text-center transition-all ${compact ? "mb-6 max-w-2xl" : "mb-8 max-w-3xl pt-6 sm:pt-10"}`}>
          <motion.div
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white/80 px-3 py-1 shadow-sm backdrop-blur"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          >
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
            <span className="text-[11px] font-mono-label font-semibold uppercase tracking-[0.2em] text-primary">Live Tracking · Real-Time Updates</span>
          </motion.div>
          <AnimatePresence initial={false}>
            {!compact && (
              <motion.div key="hero-icon" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, height: 0 }} className="mb-4 flex justify-center">
                <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-cyan-500 text-white shadow-lg shadow-primary/30 animate-floaty">
                  <Plane className="h-8 w-8 -rotate-45" />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <h1 className={`font-display font-extrabold tracking-tight leading-[1.05] ${compact ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl lg:text-6xl"}`}>Track your <span className="gradient-text">shipment</span></h1>
          {!compact && <p className="mt-3 text-sm text-muted-foreground sm:text-base">Follow the full journey — door to door, in real time.</p>}

          <form onSubmit={search} className="glow-ring mx-auto mt-6 flex max-w-2xl items-center gap-2 rounded-2xl border border-border/80 bg-white/90 p-2 shadow-[0_20px_50px_-24px_rgba(33,79,216,0.45)] backdrop-blur" data-testid="track-form">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-12 border-0 bg-transparent pl-12 pr-10 text-base font-mono-label shadow-none focus-visible:ring-0 focus-visible:border-transparent"
                placeholder="Enter AWB / tracking no."
                value={tn}
                onChange={(e) => setTn(e.target.value)}
                autoFocus
                data-testid="track-input"
              />
              {tn && <button type="button" onClick={() => { setTn(""); setError(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-secondary hover:text-foreground" data-testid="track-clear" aria-label="Clear"><X className="h-4 w-4" /></button>}
            </div>
            <Button type="submit" size="lg" className="h-12 shine-hover px-6" disabled={loading || !tn.trim()} data-testid="track-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plane className="h-4 w-4 -rotate-45" /> Track</>}
            </Button>
          </form>
        </motion.div>

        {error && !loading && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="shake mx-auto flex max-w-2xl items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-rose-700" data-testid="track-error">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-rose-100"><AlertTriangle className="h-4 w-4" /></span>
            <div><div className="text-sm font-semibold">{error}</div><div className="text-xs text-rose-600/80">Double-check the number on your AWB label or booking confirmation.</div></div>
          </motion.div>
        )}

        {loading && (
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]" data-testid="track-loading">
            <div className="panel h-72 animate-pulse bg-slate-100/60" />
            <div className="panel h-72 animate-pulse bg-slate-100/60" />
          </div>
        )}

        {result && !loading && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]" data-testid="track-result">
            {/* LEFT — status & details */}
            <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
              <div className="grain relative overflow-hidden rounded-2xl bg-[#070C16] p-5 text-white shadow-[0_30px_70px_-30px_rgba(7,12,22,0.8)] sm:p-6" data-testid="track-status-card">
                <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-primary/40 blur-3xl" />
                <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-cyan-400/20 blur-3xl" />
                <div className="relative">
                  {(result.branding?.company_name || result.source_portal) && (
                    <div className="mb-4 flex items-center gap-3 border-b border-white/10 pb-4" data-testid="track-branding">
                      {result.branding?.logo_file_id ? (
                        <img src={`${API}/portal-logo/${result.branding.logo_file_id}`} alt="" className="h-10 w-10 rounded-lg border border-white/15 bg-white object-contain" data-testid="track-logo" />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/10 font-extrabold text-white ring-1 ring-white/15">
                          {(result.branding?.company_name || result.source_portal || "S").charAt(0)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="truncate font-display font-bold leading-tight">{result.branding?.company_name || result.source_portal}</div>
                        <div className="text-xs text-white/60">{result.source_portal}{result.destination_country ? ` · ${result.origin_country || "India"} → ${result.destination_country}` : ""}</div>
                      </div>
                      <span className="ml-auto hidden sm:inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-white/70"><ShieldCheck className="h-3 w-3 text-emerald-400" /> Verified</span>
                    </div>
                  )}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-mono-label uppercase tracking-widest text-white/50">Tracking No.</div>
                      <div className="font-mono-label text-xl font-bold tracking-wide" data-testid="track-tn">{result.tracking_number}</div>
                    </div>
                    <Button size="sm" variant="outline" className="shrink-0 border-white/20 bg-white/10 text-white hover:bg-white/20 hover:text-white hover:border-white/30" onClick={copyLink} data-testid="track-copy-link-button">
                      <Link2 className="h-4 w-4" /> Copy Link
                    </Button>
                  </div>
                  <div className="mt-4 flex items-center gap-2.5">
                    <span className="relative flex h-3 w-3"><span className={`absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping ${result.delivered ? "bg-emerald-400" : "bg-cyan-400"}`} /><span className={`relative inline-flex h-3 w-3 rounded-full ${result.delivered ? "bg-emerald-400" : "bg-cyan-400"}`} /></span>
                    <span className="font-display text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="track-status">{result.status || "—"}</span>
                  </div>
                  {result.est_delivery_date && !result.delivered && (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-sm text-emerald-200" data-testid="track-eta">
                      <Clock className="h-4 w-4" /> Estimated delivery <span className="font-semibold text-white">{result.est_delivery_date}</span>
                    </div>
                  )}

                  <div className="mt-6 flex items-center justify-between text-[11px] font-mono-label font-bold uppercase tracking-wider text-white/60" data-testid="track-route">
                    <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-cyan-300" />{result.booking_city || result.origin_country || "India"}</span>
                    <span className="flex items-center gap-1.5">{result.destination_country || "Destination"}<Globe className="h-3.5 w-3.5 text-emerald-300" /></span>
                  </div>
                </div>
              </div>

              {result.delivered && (
                <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="track-delivered-banner">
                  <div className="flex items-center gap-2.5 text-emerald-800">
                    <span className="pop-in flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 ring-1 ring-emerald-500/30"><CheckCircle2 className="h-5 w-5" /></span>
                    <div><div className="text-sm font-bold">Delivered{result.delivered_location ? ` · ${result.delivered_location}` : ""}</div><div className="text-xs text-emerald-700/80">{result.delivered_at ? fmtDT(result.delivered_at) : ""}{result.delivered_received_by ? ` · received by ${result.delivered_received_by}` : ""}</div></div>
                  </div>
                  <Button size="sm" variant="success" onClick={() => setShowPod(true)} data-testid="track-download-pod"><Download className="h-4 w-4" /> Proof of Delivery</Button>
                </motion.div>
              )}

              {result.on_hold && (
                <div className="flex items-start gap-2.5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700" data-testid="track-hold-banner">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div><div className="text-sm font-bold">Shipment On Hold</div><div className="text-xs">{result.hold_reason || "Please check the updates below."}</div></div>
                </div>
              )}

              <div className="panel p-4">
                <div className="mb-3 flex items-center gap-1.5 text-[10px] font-mono-label font-semibold uppercase tracking-widest text-muted-foreground"><Package className="h-3.5 w-3.5 text-primary" /> Shipment details</div>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3" data-testid="track-details">
                  {[
                    { icon: Hash, label: "AWB No.", value: result.tracking_number || "—", testId: "track-awb", mono: true },
                    { icon: Calendar, label: "Booked On", value: fmtDT(result.created_at), sub: result.booking_city, testId: "track-booked-on" },
                    { icon: Building2, label: "Service Provider", value: result.branding?.company_name || result.source_portal || "—", testId: "track-provider" },
                    { icon: User, label: "Consignee", value: result.recipient_name || "—", testId: "track-consignee" },
                    { icon: Globe, label: "Destination", value: result.destination_country || "—", testId: "track-consignee-country" },
                    { icon: Activity, label: "Status", value: result.status || "—", testId: "track-status-text" },
                  ].map((c, i) => (
                    <motion.div key={c.label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.05 }}
                      className="group rounded-xl border border-border bg-slate-50/60 p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:bg-white hover:shadow-md">
                      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-mono-label uppercase tracking-widest text-muted-foreground">
                        <c.icon className="h-3 w-3 text-primary transition-transform group-hover:scale-110" /> {c.label}
                      </div>
                      <div className={`truncate text-sm font-semibold ${c.mono ? "font-mono-label" : ""}`} data-testid={c.testId} title={String(c.value)}>{c.value}</div>
                      {c.sub && <div className="truncate text-xs text-muted-foreground">{c.sub}</div>}
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="track-parties">
                <div className="panel hover-lift p-4 text-sm">
                  <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-mono-label uppercase tracking-widest text-muted-foreground"><span className="flex h-5 w-5 items-center justify-center rounded-md bg-primary/10 text-primary"><User className="h-3 w-3" /></span> Sender</div>
                  <div className="font-semibold">{result.sender_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{result.sender_location}</div>
                </div>
                <div className="panel hover-lift p-4 text-sm">
                  <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-mono-label uppercase tracking-widest text-muted-foreground"><span className="flex h-5 w-5 items-center justify-center rounded-md bg-emerald-500/10 text-emerald-600"><MapPin className="h-3 w-3" /></span> Receiver</div>
                  <div className="font-semibold">{result.recipient_name}</div>
                  <div className="text-xs text-muted-foreground">{result.delivery_address}</div>
                </div>
              </div>
            </div>

            {/* RIGHT — journey */}
            <div className="space-y-4">
              {result.timeline?.some((t) => t.status === "EVENT") && (
                <div className="panel p-4 sm:p-5" data-testid="track-events">
                  <div className="mb-3 flex items-center gap-1.5 text-[10px] font-mono-label font-semibold uppercase tracking-widest text-muted-foreground"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> Shipment updates</div>
                  <div className="space-y-2">
                    {result.timeline.filter((t) => t.status === "EVENT").map((t, i) => (
                      <div key={i} className={`rounded-xl border p-3 ${t.event_type === "hold" ? "border-rose-200 bg-rose-50" : t.event_type === "resume" ? "border-emerald-200 bg-emerald-50" : "border-border bg-slate-50/60"}`} data-testid={`track-event-${i}`}>
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm font-semibold ${t.event_type === "hold" ? "text-rose-700" : t.event_type === "resume" ? "text-emerald-700" : ""}`}>{t.title}</span>
                          <span className="shrink-0 text-xs text-muted-foreground">{new Date(t.timestamp).toLocaleDateString()}</span>
                        </div>
                        {t.note && <div className="mt-0.5 text-xs text-muted-foreground">{t.note}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] }} className="panel p-5 sm:p-6" data-testid="track-history-card">
                <Timeline timeline={result.timeline} status={result.status} order={result} publicView />
              </motion.div>
              <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Updates are recorded by the ShipOMS operations team at every checkpoint.
              </div>
            </div>
          </motion.div>
        )}

        {!result && !loading && !error && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mx-auto mt-10 grid max-w-3xl gap-3 sm:grid-cols-3" data-testid="track-perks">
            {[
              { icon: Activity, t: "Live checkpoint updates", d: "Every scan and status change, newest first." },
              { icon: Globe, t: "India → the world", d: "Korea, Japan, UAE and more via dedicated portals." },
              { icon: Download, t: "Proof of Delivery", d: "Download a branded POD the moment it's delivered." },
            ].map((p, i) => (
              <div key={p.t} className="panel hover-lift fade-up p-4" style={{ "--i": i + 2 }}>
                <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/15"><p.icon className="h-4 w-4" /></span>
                <div className="mt-3 font-display text-sm font-bold">{p.t}</div>
                <div className="mt-0.5 text-xs text-muted-foreground">{p.d}</div>
              </div>
            ))}
          </motion.div>
        )}
      </main>

      <Dialog open={showPod} onOpenChange={setShowPod}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="track-pod-dialog">
          <DialogHeader className="no-print"><DialogTitle className="text-2xl">Proof of Delivery</DialogTitle></DialogHeader>
          {result && <PodDocument order={result} />}
          <DialogFooter className="no-print"><Button onClick={() => window.print()} data-testid="track-pod-print"><Download className="h-4 w-4" /> Download / Print</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
